// src/components/orderentry/panels/EnterDetailsPanel.jsx
// -----------------------------------------------------------------------------
// AREA 1 — ENTER DETAILS
//
// Responsibilities:
//  - ZIP input (user types)
//  - City/State display (derived from selected address)
//  - Customer list (loaded by ZIP)
//  - Address list (loaded by selected customer)
//  - Bill-To panel (derived from selected address)
//  - Notes panel (loaded by MAS ID)
//
// This panel is intentionally "dumb": it renders UI and calls callbacks.
// Data fetching + state ownership stays in OrderEntry.jsx.
// -----------------------------------------------------------------------------

import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GridBadge from '../components/GridBadge.jsx';
import DirectShipPatientDialog from '../dialogs/DirectShipPatientDialog.jsx';
import { normalizeZipFieldValue } from '../utils/postal.js';

// -----------------------------------------------------------------------------
// Block 1.1 - Small internal helpers (render-only)
// -----------------------------------------------------------------------------
function ListContainer({ title, minWidth, height, children, boxSx }) {
  return (
    <Box sx={{ width: minWidth, minWidth, maxWidth: minWidth }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 0.15,
          lineHeight: 1.1,
          display: 'block',
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          height,
          maxHeight: height,
          minHeight: height,
          overflowY: 'auto',
          overflowX: 'hidden', // default: no horizontal scroll
          bgcolor: 'background.paper',
          whiteSpace: 'nowrap',
          ...boxSx, // allow per-list overrides
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function EmptyHint({ children }) {
  return (
    <Typography variant="body2" sx={{ p: 1 }} color="text.secondary">
      {children}
    </Typography>
  );
}

// -----------------------------------------------------------------------------
// Main panel
// -----------------------------------------------------------------------------
export default function EnterDetailsPanel({
  // UI state
  disabled,

  // ZIP / Location
  zip,
  setZip,
  city,
  state,

  // Lists
  listMinWidth, // { customer, street, billTo, notes }
  customers,
  selectedCustomer,
  onCustomerSelect, // (customer) => void
  customerListFocusNonce,

  addresses,
  selectedAddressIndex,
  onAddressClick, // (idx) => void

  // Bill-to + Notes
  billTo, // { name,address1,address2,city,state,zip } | null
  notes, // string[]

  // TMS ShipToCode=P direct-ship patient override
  directShipPatientActive,
  directShipPatientComplete,
  directShipPatient,
  directShipDialogOpen,
  directShipStudies,
  directShipStudiesLoading,
  directShipStudiesError,
  onOpenDirectShipPatient,
  onCloseDirectShipPatient,
  onSubmitDirectShipPatient,
  internationalSale,

  // (Optional) If you want to show a loading hint near lists
  lookupLoading,

  // Focus handoff helpers
  focusPoNumber,
}) {
  // Total vertical space budget for the middle column (Street+BillTo)
  const listHeight = 160; // base unit used elsewhere

  const streetHeight = Math.round(listHeight * 0.6);
  const billToHeight = Math.round(listHeight * 0.25);
  const SBMinW = (listMinWidth.billTo ?? 0) * 2;

  const customerRefs = React.useRef([]);
  const addressRefs = React.useRef([]);
  const pendingAddressFocusAfterCustomerTabRef = React.useRef(false);

  // ---------------------------------------------------------------------------
  // BLOCK ED.KEYMODE.0 — Track when user is arrowing in Customer list
  // If true, we keep focus in customer list even if parent tries to move it.
  // ---------------------------------------------------------------------------
  const customerArrowModeRef = React.useRef(false);

  // ---------------------------------------------------------------------------
  // BLOCK ED.KEYNAV.0 — Arrow-key nav that also selects (Excel parity)
  // - ArrowUp/Down moves focus AND triggers selection callback for the new row
  // - Enter/Space still work as explicit “activate”
  // ---------------------------------------------------------------------------
  const handleArrowNavigation = (event, refs, idx, onMoveSelect) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return false;
    }

    const nextIdx = event.key === 'ArrowDown' ? idx + 1 : idx - 1;
    const nextNode = refs.current[nextIdx];

    if (!nextNode) {
      return false;
    }

    event.preventDefault();
    nextNode.focus();
    nextNode.scrollIntoView?.({ block: 'nearest' });

    if (typeof onMoveSelect === 'function') {
      onMoveSelect(nextIdx);
    }

    return true;
  };

  // Normalize inputs that should be arrays to avoid defensive checks downstream.
  const customerList = React.useMemo(
    () => (Array.isArray(customers) ? customers : []),
    [customers]
  );
  const addressList = React.useMemo(() => (Array.isArray(addresses) ? addresses : []), [addresses]);
  const notesList = React.useMemo(() => (Array.isArray(notes) ? notes : []), [notes]);
  const [activeAddressIndex, setActiveAddressIndex] = React.useState(-1);

  const selectedCustomerIndex = React.useMemo(() => {
    if (!selectedCustomer) {
      return -1;
    }

    return customerList.findIndex((c) => c?.shipToName === selectedCustomer);
  }, [customerList, selectedCustomer]);

  const selectedAddressTabIndex = React.useMemo(() => {
    if (activeAddressIndex >= 0 && activeAddressIndex < addressList.length) {
      return activeAddressIndex;
    }

    if (selectedAddressIndex === null || selectedAddressIndex === undefined) {
      return addressList.length > 0 ? 0 : -1;
    }

    if (selectedAddressIndex < 0 || selectedAddressIndex >= addressList.length) {
      return -1;
    }

    return selectedAddressIndex;
  }, [activeAddressIndex, addressList.length, selectedAddressIndex]);

  const focusCustomerRow = React.useCallback(
    (preferredIndex = -1) => {
      if (!customerList.length) {
        return false;
      }

      const idx =
        preferredIndex >= 0 && preferredIndex < customerList.length
          ? preferredIndex
          : selectedCustomerIndex >= 0
            ? selectedCustomerIndex
            : 0;

      const node = customerRefs.current?.[idx];
      if (!node) {
        return false;
      }

      node.focus?.();
      node.scrollIntoView?.({ block: 'nearest' });
      return true;
    },
    [customerList.length, selectedCustomerIndex]
  );

  const focusAddressRow = React.useCallback(
    (preferredIndex = -1) => {
      if (!addressList.length) {
        return false;
      }

      const idx =
        preferredIndex >= 0 && preferredIndex < addressList.length
          ? preferredIndex
          : selectedAddressTabIndex >= 0
            ? selectedAddressTabIndex
            : 0;

      const node = addressRefs.current?.[idx];
      if (!node) {
        return false;
      }

      node.focus?.();
      node.scrollIntoView?.({ block: 'nearest' });
      return true;
    },
    [addressList.length, selectedAddressTabIndex]
  );

  // ---------------------------------------------------------------------------
  // BLOCK ED.ROVINGTAB.0 — Make selected row the only tabbable row
  // Why:
  // - Ensures the selected row actually receives keyboard events (ArrowUp/Down)
  // ---------------------------------------------------------------------------
  const getCustomerTabIndex = (c, idx) => {
    if (selectedCustomer) {
      if (selectedCustomer === c.shipToName) {
        return 2;
      }

      return selectedCustomerIndex < 0 && idx === 0 ? 2 : -1;
    }
    return idx === 0 ? 2 : -1; // fallback when nothing selected yet
  };

  React.useEffect(() => {
    if (!customerListFocusNonce || customerList.length === 0 || selectedCustomer) {
      return;
    }

    requestAnimationFrame(() => {
      const first = customerRefs.current?.[0];
      first?.focus?.();
      first?.scrollIntoView?.({ block: 'nearest' });
    });
  }, [customerListFocusNonce, customerList.length, selectedCustomer]);

  React.useEffect(() => {
    if (addressList.length === 0) {
      setActiveAddressIndex(-1);
      return;
    }

    const nextIdx =
      selectedAddressIndex !== null &&
      selectedAddressIndex !== undefined &&
      selectedAddressIndex >= 0 &&
      selectedAddressIndex < addressList.length
        ? selectedAddressIndex
        : 0;

    setActiveAddressIndex(nextIdx);

    if (pendingAddressFocusAfterCustomerTabRef.current) {
      requestAnimationFrame(() => {
        focusAddressRow(nextIdx);
      });
      pendingAddressFocusAfterCustomerTabRef.current = false;
    }
  }, [addressList, selectedAddressIndex, focusAddressRow]);

  return (
    <Box sx={{ position: 'relative' }}>
      <GridBadge n="1" />

      <Grid container spacing={2}>
        {/* LEFT COLUMN: Zip, City, State */}
        <Grid size={{ xs: 12, sm: 1.5 }}>
          <Stack spacing={1}>
            <TextField
              fullWidth
              label="Zip Code"
              value={zip}
              onChange={(e) => {
                const next = normalizeZipFieldValue(e.target.value, { internationalSale });
                setZip(next);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Tab' || e.shiftKey) {
                  return;
                }

                e.preventDefault();
                customerArrowModeRef.current = false;

                const firstCustomer = customerList[0];
                if (firstCustomer) {
                  onCustomerSelect(firstCustomer, { preserveZipUntilStreetSelection: true });
                }

                if (focusCustomerRow(0)) {
                  return;
                }

                if (focusAddressRow()) {
                  return;
                }

                if (typeof focusPoNumber === 'function') {
                  focusPoNumber();
                }
              }}
              inputProps={{ tabIndex: 1 }}
              disabled={disabled || directShipPatientActive}
            />
            <TextField fullWidth label="City" value={city} disabled />
            <TextField fullWidth label="State" value={state} disabled />
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: Customer + Street + Bill To + Notes */}
        <Grid size={{ xs: 12, sm: 10.5 }}>
          <Grid
            container
            wrap="nowrap"
            columnSpacing={2}
            sx={{
              overflowX: 'hidden',
              overflowY: 'visible',
            }}
          >
            {/* CUSTOMER LIST (fixed width; no shrink) */}
            <Grid
              size="auto"
              sx={{
                flex: '0 0 auto',
              }}
            >
              <ListContainer title="Customer" minWidth={listMinWidth.customer} height={listHeight}>
                {lookupLoading && customerList.length === 0 && <EmptyHint>Loading…</EmptyHint>}

                {!lookupLoading && customerList.length === 0 && (
                  <EmptyHint>Type a ZIP/postal prefix (3+ characters) to load customers.</EmptyHint>
                )}

                {customerList.map((c, idx) => (
                  <Box
                    key={`${c.customerNo}-${c.shipToName}`}
                    sx={{
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      bgcolor:
                        selectedCustomer === c.shipToName ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom:
                        idx < customerList.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    role="button"
                    tabIndex={getCustomerTabIndex(c, idx)}
                    ref={(node) => {
                      customerRefs.current[idx] = node;
                    }}
                    onClick={(e) => {
                      customerArrowModeRef.current = false;
                      e.currentTarget.focus();
                      onCustomerSelect(c, { preserveZipUntilStreetSelection: true });
                    }}
                    onFocus={() => {
                      if (!customerArrowModeRef.current) return;
                      onCustomerSelect(c, { preserveZipUntilStreetSelection: true });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        customerArrowModeRef.current = true;
                      }

                      if (
                        handleArrowNavigation(e, customerRefs, idx, (nextIdx) => {
                          const next = customerList[nextIdx];
                          if (!next) return;

                          requestAnimationFrame(() => {
                            if (customerArrowModeRef.current) {
                              customerRefs.current?.[nextIdx]?.focus?.();
                            }
                          });
                        })
                      ) {
                        return;
                      }

                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        customerArrowModeRef.current = false;
                        onCustomerSelect(c, { preserveZipUntilStreetSelection: true });
                        return;
                      }

                      if (e.key === 'Tab') {
                        if (!e.shiftKey) {
                          e.preventDefault();
                          customerArrowModeRef.current = false;
                          onCustomerSelect(c, { preserveZipUntilStreetSelection: true });
                          pendingAddressFocusAfterCustomerTabRef.current = true;

                          if (focusAddressRow()) {
                            return;
                          }

                          // Keep focus in customer list until address rows are loaded,
                          // then the effect will move focus directly to Street Address.
                          return;
                        }

                        customerArrowModeRef.current = false;
                        onCustomerSelect(c, { preserveZipUntilStreetSelection: true });
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={c.shipToName}
                    >
                      {c.shipToName}
                    </Typography>
                  </Box>
                ))}
              </ListContainer>
            </Grid>

            {/* STREET + BILLTO (fixed width; no shrink) */}
            <Grid
              size="auto"
              sx={{
                flex: '0 0 auto',
              }}
            >
              <Stack spacing={1}>
                <ListContainer
                  title="Street Address"
                  minWidth={SBMinW}
                  height={streetHeight}
                  boxSx={{
                    overflowX: 'auto',
                    overflowY: 'auto', // keep vertical
                    scrollbarWidth: 'thin', // Firefox
                    '&::-webkit-scrollbar': {
                      height: 1, // slim horizontal bar
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      borderRadius: 8,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: 'rgba(0,0,0,0.35)',
                    },
                  }}
                >
                  {addressList.length === 0 && (
                    <EmptyHint>Select a customer to view ship-to addresses.</EmptyHint>
                  )}

                  {addressList.map((addr, idx) => (
                    <Box
                      key={`${addr.customerNo}-${addr.shipToCode}-${addr.shipToAddress1 || ''}-${addr.shipToAddress2 || ''}`}
                      sx={{
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        minWidth: 0,
                        bgcolor:
                          selectedAddressTabIndex === idx ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderBottom:
                          idx < addressList.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                      }}
                      onClick={() => {
                        setActiveAddressIndex(idx);
                        onAddressClick(idx);
                      }}
                      role="button"
                      tabIndex={
                        selectedAddressTabIndex >= 0
                          ? idx === selectedAddressTabIndex
                            ? 3
                            : -1
                          : idx === 0
                            ? 3
                            : -1
                      }
                      ref={(node) => {
                        addressRefs.current[idx] = node;
                      }}
                      onFocus={() => {
                        setActiveAddressIndex(idx);
                      }}
                      onKeyDown={(e) => {
                        if (
                          handleArrowNavigation(e, addressRefs, idx, (nextIdx) => {
                            setActiveAddressIndex(nextIdx);
                          })
                        ) {
                          return;
                        }

                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onAddressClick(idx);

                          if (typeof focusPoNumber === 'function') {
                            focusPoNumber();
                          }
                          return;
                        }

                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();

                          if (typeof focusPoNumber === 'function') {
                            focusPoNumber();
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 80 }}>
                        {addr.customerNo}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
                        {addr.shipToCode}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          flex: '0 0 auto', // allow it to be as wide as needed
                          whiteSpace: 'nowrap', // single line so horizontal scroll works
                          overflow: 'visible', // don't clip; let container scroll
                        }}
                        title={[
                          addr.shipToAddress1,
                          addr.shipToAddress2,
                          addr.city,
                          addr.state,
                          addr.zipCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      >
                        {[
                          addr.shipToAddress1,
                          addr.shipToAddress2,
                          addr.city,
                          addr.state,
                          addr.zipCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    </Box>
                  ))}
                </ListContainer>

                <ListContainer title="Bill To" minWidth={SBMinW} height={billToHeight}>
                  {!billTo && <EmptyHint>Select a street address to view Bill To.</EmptyHint>}

                  {billTo && (
                    <Box sx={{ px: 1, py: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={[
                          billTo.name,
                          billTo.address1,
                          billTo.address2,
                          [billTo.city, billTo.state].filter(Boolean).join(', '),
                          billTo.zip,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      >
                        {[
                          billTo.name,
                          billTo.address1,
                          billTo.address2,
                          [billTo.city, billTo.state].filter(Boolean).join(', '),
                          billTo.zip,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </Typography>
                    </Box>
                  )}
                </ListContainer>
              </Stack>
            </Grid>

            {/* NOTES (fixed width; no shrink) */}
            <Grid
              size="auto"
              sx={{
                flex: '0 0 auto',
              }}
            >
              <Box sx={{ width: listMinWidth.notes, minWidth: listMinWidth.notes }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Notes
                </Typography>

                <Box
                  tabIndex={-1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: `calc(${listHeight}px )`,
                    maxHeight: `calc(${listHeight}px )`,
                    minHeight: `calc(${listHeight}px )`,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    bgcolor: 'background.paper',
                    px: 1,
                    py: 0.5,
                    outline: 'none',
                    userSelect: 'text',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {notesList.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Notes will appear here when a MAS ID is selected.
                    </Typography>
                  )}

                  {notesList.map((line) => (
                    <Typography
                      key={`note-${line || 'blank'}`}
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {line}
                    </Typography>
                  ))}

                  {directShipPatientActive && (
                    <Box
                      sx={{
                        mt: 1,
                        border: '1px solid',
                        borderColor: directShipPatientComplete ? 'success.main' : 'warning.main',
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2">Direct Ship Patient</Typography>

                          {directShipPatientComplete ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {directShipPatient?.shipToName}
                              </Typography>
                              <Typography variant="body2">{directShipPatient?.address1}</Typography>
                              <Typography variant="body2">{directShipPatient?.address2}</Typography>
                              <Typography variant="body2">
                                {[directShipPatient?.city, directShipPatient?.state]
                                  .filter(Boolean)
                                  .join(', ')}{' '}
                                {directShipPatient?.zip}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="warning.main">
                              Patient address is required before final submit.
                            </Typography>
                          )}
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={onOpenDirectShipPatient}
                          sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
                        >
                          {directShipPatientComplete ? 'Edit' : 'Enter Address'}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <DirectShipPatientDialog
        open={directShipDialogOpen}
        value={directShipPatient}
        studies={directShipStudies}
        studiesLoading={directShipStudiesLoading}
        studiesError={directShipStudiesError}
        onClose={onCloseDirectShipPatient}
        onSubmit={onSubmitDirectShipPatient}
      />
    </Box>
  );
}
